import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Button, Box, Typography } from '@material-ui/core';

import { useBackendContext } from '@omq/shared';

import { Headline } from '@admin/shared/components/typography/headline';
import { Form, FormRow } from '@admin/shared/components/forms/form';
import { PasswordInput } from '@admin/shared/components/inputs/text-field/password';
import type { ValidationState } from '@admin/shared/util/validation';
import { LoadingIndicator } from '@admin/shared/components/progress/loading-indicator';
import { Validation, ValidationErrors } from '@admin/shared/util/validation';
import { DialogSizes, useDialogStyles } from '@admin/shared/components/dialogs/dialog';
import { LockIcon } from '@admin/shared/components/icons/custom/lock';
import { useDialogContext } from '@admin/shared/context/dialog';
import { LoginAPI } from '@admin/shared/api/login';
import { Link } from '@admin/shared/components/router/link';

import { LOGIN_ROUTES } from '@admin/login/login';

// config of form validation
const validationConfig = {
  password: {
    isRequired: true,
  },
  confirmPassword: {
    isRequired: true,
  },
};

/**
 * Component for reset password form.
 *
 * @returns {JSX.Element}
 */
export function ResetPasswordForm(): JSX.Element {
  const { t } = useTranslation(['login']);
  const { search } = useLocation();
  const backend = useBackendContext();

  const dialogStyles = useDialogStyles();
  const { showDialog } = useDialogContext();

  // define states
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [validationState, setValidationState] = useState<ValidationState>({});
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // read url params
  const searchParams = new URLSearchParams(search);
  const expiration = searchParams.get('expiration') || '';
  const signature = searchParams.get('signature') || '';
  const user = searchParams.get('user') || '';

  // handle form submit
  const handlePasswordReset = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      setIsLoading(true);
      await LoginAPI.updatePassword(backend, user, password, {
        user,
        expiration,
        signature,
      }).finally(() => {
        setIsLoading(false);
      });

      setFormSubmitted(true);
    } catch (ex) {
      // show error
      showDialog({
        size: DialogSizes.SMALL,
        children: (
          <Box display="flex" flexDirection="column" alignItems="center">
            <LockIcon className={dialogStyles.icon} />
            <p className={dialogStyles.headline}>{t('dialogs.passwordResetFailed.title')}</p>
            <p className={dialogStyles.text}>{t('dialogs.passwordResetFailed.text')}</p>
          </Box>
        ),
        actions: [
          {
            label: t('login:buttons.ok'),
            color: 'primary',
            autoFocus: true,
          },
        ],
      });
    }
  };

  // validate form input
  const handleFormValidation = () => {
    const error = Validation.validateRecordWithConfig(
      {
        password,
        confirmPassword,
      },
      validationConfig,
    );

    if (password !== confirmPassword) {
      error.confirmPassword = ValidationErrors.PASSWORD_MISMATCH;
    }

    setValidationState(error);
    return Object.keys(error).length === 0;
  };

  // render content
  let formContent = null;

  // if security params & user information are missing
  // render error messages
  if (expiration === '' || signature === '' || user === '') {
    formContent = (
      <Form>
        <FormRow margin="large">
          <Headline size="large">{t('login:headlines.resetPasswordInvalidParams')}</Headline>
          <Box mt={3}>
            <Typography variant="body1">{t('login:texts.resetPasswordInvalidParams')}</Typography>
          </Box>
        </FormRow>

        <FormRow>
          <Link to={LOGIN_ROUTES.LOGIN}>
            <Button variant="contained" color="primary">
              {t('login:links.login')}
            </Button>
          </Link>
        </FormRow>
      </Form>
    );
  } else if (formSubmitted) {
    // render result if password has been reset successfully
    formContent = (
      <Form>
        <FormRow margin="large">
          <Headline size="large">{t('login:headlines.resetPasswordResult')}</Headline>
          <Box mt={3}>
            <Typography variant="body1">{t('login:texts.resetPasswordResult')}</Typography>
          </Box>
        </FormRow>

        <FormRow>
          <Link to={LOGIN_ROUTES.LOGIN}>
            <Button variant="contained" color="primary">
              {t('login:links.login')}
            </Button>
          </Link>
        </FormRow>
      </Form>
    );
  } else {
    // render form
    formContent = (
      <Form onSubmit={handlePasswordReset} customValidation={handleFormValidation}>
        <FormRow margin="large">
          <Headline size="large">{t('login:headlines.resetPassword')}</Headline>
        </FormRow>

        <FormRow>
          <PasswordInput
            placeholder={t('login:inputs.password')}
            value={password}
            validationConfig={validationConfig.password}
            errorMessage={validationState.password}
            onChange={(evt) => {
              setValidationState({});
              setPassword(evt.target.value);
            }}
          />
        </FormRow>

        <FormRow>
          <PasswordInput
            placeholder={t('login:inputs.confirmPassword')}
            value={confirmPassword}
            validationConfig={validationConfig.confirmPassword}
            errorMessage={validationState.confirmPassword}
            onChange={(evt) => {
              setValidationState({});
              setConfirmPassword(evt.target.value);
            }}
          />
        </FormRow>

        <FormRow>
          <Button variant="contained" color="primary" type="submit">
            {t('login:buttons.reset')}
          </Button>
        </FormRow>
      </Form>
    );
  }

  // render component
  return (
    <Box>
      {formContent}
      {isLoading && <LoadingIndicator />}
    </Box>
  );
}
